import { FC, Fragment, ReactNode, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Link } from 'react-router-dom';
import { chunk } from 'lodash';
import { createBemBlockBuilder, ShowMorePagination } from '@ebsco-ui/ebsco-ui';

import { usePaginationItemFocus } from '@app/hooks';
import {
  courseReservesMessages,
  getPageMessages,
  sharedMessages,
} from '@app/translations';
import { FOCUSED_ELEMENT_SELECTOR, RECORDS_PER_PAGE } from '@app/constants';

import { BrowseByComponent } from './constants';

const instructorsListLimit = RECORDS_PER_PAGE;
const cnBem = createBemBlockBuilder(['browseBy']);

export const BrowseByInstructors: FC<BrowseByComponent> = ({
  records,
  getSearchPath,
}) => {
  const [instructorsPageNumber, setInstructorsPageNumber] = useState(1);
  const { $t } = useIntl();
  const { browseByInstructor } = courseReservesMessages;

  const { shouldBeFocused } = usePaginationItemFocus({
    pageNumber: instructorsPageNumber,
    totalPagesNumber: instructorsPageNumber,
    selector: FOCUSED_ELEMENT_SELECTOR,
    shouldResetState: true,
  });

  const allPages = chunk(records, instructorsListLimit);
  const visiblePages = allPages.slice(0, instructorsPageNumber);

  const instructorsColumns = useMemo<
    {
      label: string;
      formatter: (instructor: BrowseByComponent['records'][0]) => ReactNode;
    }[]
  >(
    () => [
      {
        label: 'Name',
        formatter: instructor => instructor.name,
      },
    ],
    []
  );

  return (
    <section className={cnBem('__instructorsWrapper')}>
      <h2 className={cnBem('__title')}>
        <FormattedMessage {...browseByInstructor} />
      </h2>
      <div
        role="grid"
        aria-label={$t(browseByInstructor)}
        aria-colcount={instructorsColumns.length}
      >
        <ShowMorePagination
          pagesContainerTag="div"
          uniqueName="instructors"
          className={cnBem('__instructors')}
          error={undefined}
          messages={getPageMessages($t, {
            showMoreButtonTitle: $t(sharedMessages.showMoreButtonTitle),
          })}
          isLoading={false}
          totalRecords={records.length}
          pages={visiblePages}
          onLoadPage={() =>
            setInstructorsPageNumber(prevState => prevState + 1)
          }
        >
          {(records: BrowseByComponent['records'], currentPageNumber) => (
            <Fragment key={currentPageNumber}>
              {currentPageNumber === 0 && (
                <div role="rowgroup" className={cnBem('__instructorsHeader')}>
                  <div role="row">
                    {instructorsColumns.map(column => (
                      <div role="columnheader" key={column.label}>
                        {column.label}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {records.map((record, index) => (
                <div role="rowgroup" key={record.id}>
                  <div
                    role="row"
                    data-auto="row"
                    aria-rowindex={
                      currentPageNumber * instructorsListLimit + index + 1
                    }
                  >
                    {instructorsColumns.map((column, columnIndex) => (
                      <div
                        role="cell"
                        className={cnBem('__instructorsCell')}
                        key={columnIndex}
                      >
                        <Link
                          to={getSearchPath(record.name)}
                          className="underlinedLink"
                          {...(shouldBeFocused &&
                            !index && {
                              'data-focus': 'focusedNode',
                            })}
                        >
                          {column.formatter(record)}
                        </Link>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </Fragment>
          )}
        </ShowMorePagination>
      </div>
    </section>
  );
};
